import { FeatureBoltRectangle3D } from 'features/assetViz/objects/features/procedural/FeatureBoltRectangle3D'
import { FeatureBox3D } from 'features/assetViz/objects/features/procedural/FeatureBox3D'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoRectangularManway,
} from 'types'

import { FeatureLabel3D } from './procedural/FeatureLabel3D'
import { SurfaceFeature3D } from './SurfaceFeature3D'

/**
 * A component representing a rectangular manway on the surface of a component
 * @param model              The rectangular manway model (from the asset model)
 * @constructor
 */
export const RectangularManwayFeature3D: React.FC<{
  model: GeckoRectangularManway
}> = ({ model }) => {
  //----------------------------------------------------------------------------

  const { bounds } = useSurfaceProps()

  const flangeWidth = model.width?.plus(
    GeckoMeasurement.CreateFromValue(0.025, model.width.unit)
  )
  const flangeHeight = model.height?.plus(
    GeckoMeasurement.CreateFromValue(0.025, model.height.unit)
  )
  const repadThickness = GeckoMeasurement.CreateFromValue(
    0.005,
    GeckoMeasurementUnit.Meters
  )
  const protrusion = React.useMemo(() => 0.1, [])

  //----------------------------------------------------------------------------

  const { labelCoords, positionLabelAbove } = React.useMemo(() => {
    const x = model.geometry.x
    let y = model.geometry.y
      .plus(model.height)
      .plus(GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Inches))
    const positionAbove =
      bounds.y.end.minus(y).num > bounds.height.scale(1 / 10).num
    if (!positionAbove) {
      y = model.geometry.y.minus(model.height)
    }
    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      x,
      y
    )
    return { labelCoords, positionLabelAbove: positionAbove }
  }, [model, bounds])

  //----------------------------------------------------------------------------

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelAbove ? 'bottom' : 'top'}
      />
      <SurfaceFeature3D model={model}>
        {model.width && model.height && flangeWidth && flangeHeight && (
          <>
            {/* Repad */}
            <FeatureBox3D
              feature={model}
              position={[0, 0, 0]}
              width={model.width.num * 1.5}
              height={model.height.num * 1.5}
              depth={repadThickness.num}
              debugColor={'blue'}
            />
            {/* Tube */}
            <FeatureBox3D
              feature={model}
              position={[0, repadThickness.num + 0.01, 0]}
              width={model.width.num}
              height={model.height.num}
              depth={protrusion - 0.025 - repadThickness.num}
              debugColor={'purple'}
            />
            {/* Inner Flange */}
            <FeatureBox3D
              feature={model}
              position={[0, protrusion - 0.04, 0]}
              width={flangeWidth.num}
              height={flangeHeight.num}
              depth={0.02}
              debugColor={'orange'}
            />
            {/* Outer Flange */}
            <FeatureBox3D
              feature={model}
              position={[0, protrusion - 0.02, 0]}
              width={flangeWidth.num}
              height={flangeHeight.num}
              depth={0.02}
              debugColor={'red'}
            />
            {/* Flange bolts */}
            <FeatureBoltRectangle3D
              feature={model}
              center={[0, protrusion, 0]}
              width={model.width}
              height={model.height}
            />
          </>
        )}
      </SurfaceFeature3D>
    </>
  )
}
